import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import SiteDiaryFields from "@/components/SiteDiaryFields";
import SignaturePad from "@/components/SignaturePad";
import BrandLogo from "@/components/BrandLogo";
import SyncChip from "@/components/SyncChip";
import FieldHomeLink from "@/components/FieldHomeLink";
import { CheckCircle, LockKey, CalendarBlank, CloudArrowUp } from "@phosphor-icons/react";
import OfflineIndicator from "@/components/OfflineIndicator";
import StaleDataBanner from "@/components/StaleDataBanner";
import InstallPrompt from "@/components/InstallPrompt";
import { queueSubmission } from "@/lib/outbox";
import { cachePut, cacheGetVerified } from "@/lib/refcache";

const today = () => new Date().toISOString().slice(0, 10);

export default function SiteDiaryCapture() {
  const [params] = useSearchParams();
  const projectId = params.get("project");

  const [pin, setPin] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [date, setDate] = useState(today());
  const [diary, setDiary] = useState(null);
  const [plantAuto, setPlantAuto] = useState([]);
  const [personnelAuto, setPersonnelAuto] = useState([]);
  const [delayCauses, setDelayCauses] = useState([]);
  const [signName, setSignName] = useState("");
  const [signSig, setSignSig] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [queuedOffline, setQueuedOffline] = useState(false);
  const [staleAt, setStaleAt] = useState(null);
  const [error, setError] = useState("");

  const applyDiary = (data) => {
    setDiary(data);
    setPlantAuto(data.plant_auto || []);
    setPersonnelAuto(data.personnel_auto || []);
    setDelayCauses(data.delay_causes || []);
  };

  const fetchDiary = async (d, thePin) => {
    const { data } = await api.get(`/public/site-diary/${projectId}`, { params: { pin: thePin, date: d } });
    applyDiary(data);
    setStaleAt(null);
    cachePut(`diary:${projectId}`, { payload: data, date: d }, thePin).catch(() => {});
  };

  const unlock = async () => {
    setError("");
    if (!projectId) { setError("Missing project — please use the site's diary link."); return; }
    if (!pin) { setError("Enter the site PIN."); return; }
    setVerifying(true);
    try { await fetchDiary(date, pin); setUnlocked(true); }
    catch (e) {
      if (!e.response) {
        const hit = await cacheGetVerified(`diary:${projectId}`, pin).catch(() => null);
        if (hit) {
          applyDiary(hit.data.payload);
          setDate(hit.data.date);
          setStaleAt(hit.cached_at);
          setUnlocked(true);
        } else {
          setError("You're offline and this site's diary hasn't been opened on this device before (or the PIN doesn't match). Connect once to enable offline use.");
        }
      } else {
        setError(formatApiErrorDetail(e.response?.data?.detail));
      }
    }
    setVerifying(false);
  };

  const changeDate = async (d) => {
    setDate(d);
    try { await fetchDiary(d, pin); }
    catch (e) {
      if (!e.response) setError("This date isn't available offline — reconnect to load other days.");
      else setError(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const submit = async () => {
    setError("");
    if (!signName) { setError("Supervisor name required to sign off."); return; }
    setSaving(true);
    const { weather, personnel, plant, activities, delays, safety } = diary;
    const body = {
      pin, weather, personnel, plant, activities, delays, safety,
      signoff_name: signName, signoff_sig: signSig,
      client_ref: crypto.randomUUID(),
      captured_at: new Date().toISOString(),
    };
    try {
      await api.post(`/public/site-diary/${projectId}?date=${date}`, body);
      setDone(true);
    } catch (e) {
      if (!e.response) {
        await queueSubmission("site_diary", `/public/site-diary/${projectId}?date=${date}`, body);
        setQueuedOffline(true);
      } else {
        setError(formatApiErrorDetail(e.response?.data?.detail));
      }
    }
    setSaving(false);
  };

  const lbl = "text-[10.5px] font-bold uppercase tracking-[0.1em] text-ink-muted mb-1.5 block";

  if (queuedOffline) {
    return (
      <div className="min-h-screen bg-equipr-black flex items-center justify-center p-6">
        <div className="bg-white w-full max-w-sm p-8 text-center" data-testid="diary-queued">
          <CloudArrowUp size={56} weight="fill" className="text-amber-500 mx-auto" />
          <h2 className="text-xl font-bold text-equipr-black mt-3">Saved on this device</h2>
          <p className="text-sm text-gray-500 mt-1">No coverage right now — the diary will send automatically when you're back in signal.</p>
          <button onClick={() => window.location.reload()} data-testid="diary-queued-done" className="w-full h-[54px] mt-6 bg-equipr-yellow text-equipr-black font-bold text-[16px] uppercase tracking-[0.04em] rounded-[12px] border border-accent-edge">Done</button>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-equipr-black flex items-center justify-center p-6">
        <div className="bg-white w-full max-w-sm p-8 text-center" data-testid="diary-success">
          <CheckCircle size={56} weight="fill" className="text-green-500 mx-auto" />
          <h2 className="text-xl font-bold text-equipr-black mt-3">Site diary signed off</h2>
          <p className="text-sm text-gray-500 mt-1">{diary?.project_name} · {date}</p>
          <button onClick={() => window.location.reload()} data-testid="diary-restart" className="w-full h-[54px] mt-6 bg-equipr-yellow text-equipr-black font-bold text-[16px] uppercase tracking-[0.04em] rounded-[12px] border border-accent-edge">Done</button>
        </div>
      </div>
    );
  }

  if (!unlocked) {
    return (
      <div className="min-h-screen bg-equipr-black flex flex-col">
        <InstallPrompt />
        <div className="px-4 py-3 flex items-center justify-between"><BrandLogo variant="dark" showProduct={false} /><SyncChip /></div>
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="bg-white w-full max-w-sm p-8" data-testid="diary-pin-gate">
            <div className="flex items-center gap-2 text-equipr-black mb-1">
              <LockKey size={22} weight="fill" className="text-accent-ink" />
              <h2 className="text-lg font-bold">Site Diary</h2>
            </div>
            <p className="text-sm text-gray-500 mb-5">Enter the site PIN to complete today's diary.</p>
            {error && <div className="border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2 mb-3" data-testid="diary-pin-error">{error}</div>}
            <label className={lbl}>Site PIN</label>
            <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric" placeholder="••••••" data-testid="diary-pin-input"
              onKeyDown={(e) => e.key === "Enter" && unlock()}
              className="w-full h-[54px] px-[15px] border-[1.5px] border-border-control bg-white text-center text-2xl tracking-[0.5em] rounded-[12px] focus:border-ink-primary focus:outline-none focus:shadow-[0_0_0_3px_rgba(255,209,8,0.35)]" />
            <button onClick={unlock} disabled={verifying || !projectId} data-testid="diary-pin-submit"
              className="w-full h-[54px] mt-5 bg-equipr-yellow text-equipr-black font-bold text-[16px] uppercase tracking-[0.04em] rounded-[12px] border border-accent-edge hover:bg-equipr-yellow-dark active:translate-y-px disabled:opacity-60">
              {verifying ? "Checking…" : "Open Diary"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-page pb-8">
      <OfflineIndicator />
      <StaleDataBanner cachedAt={staleAt} />
      <InstallPrompt />
      <div className="bg-equipr-black text-white px-4 py-3 flex items-center justify-between sticky top-0 z-10">
        <BrandLogo variant="dark" showProduct={false} />
        <span className="flex items-center gap-3"><FieldHomeLink variant="dark" /><SyncChip /><span className="text-sm font-semibold text-equipr-yellow">Site Diary</span></span>
      </div>
      <div className="max-w-2xl mx-auto p-4 space-y-4">
        <div className="bg-white border border-gray-200 p-4 flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-sm font-bold text-equipr-black">{diary?.project_name}</div>
            {diary?.status === "signed" && <span className="text-[11px] font-bold text-green-700">Signed by {diary.signoff?.name}</span>}
          </div>
          <div className="flex items-center gap-2">
            <CalendarBlank size={16} className="text-gray-500" />
            <input type="date" value={date} onChange={(e) => changeDate(e.target.value)} data-testid="diary-date"
              className="h-[54px] px-[15px] border-[1.5px] border-border-control bg-white text-[16px] rounded-[12px] focus:border-ink-primary focus:ring-2 focus:ring-equipr-yellow/40 focus:outline-none" />
          </div>
        </div>

        {error && <div className="border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2" data-testid="diary-error">{error}</div>}

        {diary && <SiteDiaryFields diary={diary} onChange={setDiary} delayCauses={delayCauses} plantAuto={plantAuto} personnelAuto={personnelAuto} />}

        <div className="bg-white border border-gray-200 p-4">
          <h4 className="text-[13px] font-bold text-equipr-black mb-2">Supervisor sign-off *</h4>
          <input className="w-full h-[54px] px-[15px] border-[1.5px] border-border-control bg-white text-[16px] rounded-[12px] focus:border-ink-primary focus:ring-2 focus:ring-equipr-yellow/40 focus:outline-none mb-2"
            placeholder="Supervisor name" value={signName} onChange={(e) => setSignName(e.target.value)} data-testid="diary-sign-name" />
          <SignaturePad onChange={setSignSig} testid="diary-sign-sig" />
        </div>

        <button onClick={submit} disabled={saving} data-testid="diary-submit"
          className="w-full h-[54px] bg-equipr-yellow text-equipr-black font-bold text-[16px] uppercase tracking-[0.04em] rounded-[12px] border border-accent-edge hover:bg-equipr-yellow-dark active:translate-y-px disabled:opacity-60">
          {saving ? "Saving…" : "Save & Sign Off Diary"}
        </button>
      </div>
    </div>
  );
}
